import { DataImportRunStartOption, DataSourceType, FeatureFlag } from "@coeff/api";

// SQL data source types that always run async. Mirrors `spec.SQL_DATA_SOURCE_TYPES`
// on the backend. Duplicated here (rather than imported from `./index`) to keep
// this module free of circular deps.
export const SQL_DATA_SOURCE_TYPES: readonly DataSourceType[] = [
  "mysql",
  "aurora_mysql",
  "cloud_sql_mysql",
  "redshift",
  "postgres",
  "aurora_postgres",
  "cloud_sql_postgres",
  "supabase",
  "neon",
  "timescaledb",
  "azure_postgres",
  "azure_mysql",
  "clickhouse",
  "snowflake",
  "mssql",
  "azure_sql",
  "bigquery",
  "databricks",
  "arrow_flight_sql",
  "motherduck",
];

// Data source types that always run in WaitIfInProgress mode regardless of
// feature flags. Mirrors the "Async enabled for everyone" branch in the
// backend map (`_IMPORT_START_OPTION_BY_DATA_SOURCE_TYPE`), plus `looker` and
// `outreach`: those are not in the backend map (its default for them would be
// LegacySyncMode), but the addon has always refreshed them async
// (`ASYNC_REFRESH_ENABLED_NON_SQL_DATA_SOURCE_TYPES`) and the backend honors
// the start option the client sends, so we keep them async here. `excel` is
// still gated on the backend (`enable_excel_async_import`, 100% in prod).
const ALWAYS_ASYNC_DATA_SOURCE_TYPES: readonly DataSourceType[] = [
  ...SQL_DATA_SOURCE_TYPES,
  "looker",
  "outreach",
  "gmail",
  "stripe",
  "netsuite",
  "excel",
  "smartlead",
  "linkedin",
  "google_sheets",
];

// Data source types whose sync mode is gated by a statsig feature flag: when
// the gate is on, the import runs as WaitIfInProgress; otherwise LegacySyncMode.
const FLAG_GATED_DATA_SOURCE_TYPES: ReadonlyArray<readonly [DataSourceType, FeatureFlag]> = [
  ["hubspot", FeatureFlag.EnableHubspotAsyncImport],
  ["tableau", FeatureFlag.EnableTableauAsyncImport],
  ["salesforce", FeatureFlag.EnableAsyncRefreshForSalesforceManualImport],
  ["custom_api", FeatureFlag.EnableAsyncRefreshForCustomApiManualImport],
  ["gong", FeatureFlag.EnableAsyncRefreshForGongManualImport],
  ["netsuite_crx", FeatureFlag.EnableAsyncImportsForNetsuiteAgent],
  ["chargebee", FeatureFlag.EnableAsyncRefreshForChargebeeManualImport],
  ["sage", FeatureFlag.EnableAsyncRefreshForSageManualImport],
  ["campfire", FeatureFlag.EnableAsyncRefreshForCampfireManualImport],
  ["coda", FeatureFlag.EnableAsyncRefreshForCodaManualImport],
];

export type SyncModeFeatureConfig = Readonly<Partial<Record<FeatureFlag, boolean>>>;

/**
 * Pure function mirroring the backend's `_get_default_start_option_for_import`
 * (`coeff/server/controllers/data_import_controller.py`), except that `looker`
 * and `outreach` are additionally treated as always-async (see
 * `ALWAYS_ASYNC_DATA_SOURCE_TYPES` above).
 *
 * Given a data source type and already-resolved feature gate booleans, returns
 * the `DataImportRunStartOption` the backend would pick by default for a
 * manual run of that data source.
 *
 * In the addon UI, build the second argument with `useSyncModeFeatureConfig()`
 * (under `StatsigSynchronousProvider`, same as `useFeatureGate`). Server and
 * tests can pass a plain object. Do not pass raw `clientFlags` here.
 *
 * Use this instead of trusting `options.import_start_option` from arbitrary
 * callers, or instead of re-implementing the same backend logic at each
 * frontend call site.
 */
export function getSyncMode(
  dataSourceType: DataSourceType,
  syncModeFeatureConfig: SyncModeFeatureConfig | undefined
): DataImportRunStartOption {
  if (ALWAYS_ASYNC_DATA_SOURCE_TYPES.indexOf(dataSourceType) >= 0) {
    return DataImportRunStartOption.WaitIfInProgress;
  }

  for (const [dst, flag] of FLAG_GATED_DATA_SOURCE_TYPES) {
    if (dst === dataSourceType) {
      return syncModeFeatureConfig?.[flag] === true
        ? DataImportRunStartOption.WaitIfInProgress
        : DataImportRunStartOption.LegacySyncMode;
    }
  }

  return DataImportRunStartOption.LegacySyncMode;
}

/**
 * True when the data source's sync mode depends on a statsig gate. Callers
 * that cannot resolve gates (e.g. GAS server code) should send no
 * `import_start_option` for these sources and let the backend decide.
 */
export const isSyncModeFeatureGated = (dataSourceType: DataSourceType): boolean =>
  FLAG_GATED_DATA_SOURCE_TYPES.some(([dst]) => dst === dataSourceType);

export const isAsyncSyncMode = (
  dataSourceType: DataSourceType,
  syncModeFeatureConfig: SyncModeFeatureConfig | undefined
): boolean =>
  getSyncMode(dataSourceType, syncModeFeatureConfig) !== DataImportRunStartOption.LegacySyncMode;
